import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  CTA_CTE,
  ENVIO_A_CARGO_COMPRADOR,
  MERCADO_PAGO
} from '../../../constants/mediosEnviosPagos.js'
import {
  MENSAJE_TIPO_PEDIDO_COMPRA,
  MENSAJE_TIPO_PEDIDO_VENTA
} from '../../../constants/mensajesTipos.js'
import {
  ID_ESTADO_CANCELADO,
  ID_ESTADO_DEVUELTO,
  ID_ESTADO_EN_ESPERA,
  ID_ESTADO_ENTREGA_OBSERVADA,
  ID_ESTADO_ENTREGADO,
  ID_ESTADO_NO_CONFIRMADO,
  ID_ESTADO_MONTOS_NO_OK,
  ID_ESTADO_MONTOS_OK,
  ID_ESTADO_PEDIDO_CREADO
} from '../../../constants/pedidoEstados'
import { AUMENTO_TIENDA_OFICIAL } from '../../../constants/tiendaOficial.js'
import { toastErrorLog } from '../../../helpers/toastErrorLog'
import { useAuth } from '../../../hooks/useAuth'
import { setModal } from '../../../slices/modalSlice'
import { DetallePrecio } from '../../compras/DetallePrecio'
import { calcularTotalItemConIva, calcularTotalItemSinIva } from '../../helpers/calcularPrecioTotal'
import * as archivoAdjuntoService from '../../servicios/archivoAdjuntoService'
import * as impuestoService from '../../servicios/impuestoService'
import * as mensajesService from '../../servicios/mensajesService'
import * as mercadoPagoService from '../../servicios/mercadoPagoService.js'
import * as pedidosService from '../../servicios/pedidosService'
import * as usuarioService from '../../servicios/usuarioService'
import BordeDetalle from '../../ui/BordeDetalle'
import { MensajeriaNew } from '../mensajeria/MensajeriaNew'
import { GenericModal } from '../modal/GenericModal'
import { PasarelaMP } from '../pasarelaPagos/PasarelaMP'
import { adjuntoBuilder } from '../upload/adjuntoBuilder'
import { Upload } from '../upload/Upload'
import styles from './Pedido.module.css'

const TIPO_IMPUESTO_PERCEPCION = 'P'
const ALICUOTA_MAX = 100
const ADJUNTO_FACTURA = 11
const PAGO_ANTICIPO = 7
const PAGO_OK = 1
const MIN_ITEMS_ENVIO_A_CARGO = 2
const IVA_ENVIO = 1.21

const recalcularMonto = (base, alicuota) => (base * alicuota) / 100

const provinciaSinSeleccionar = (idProvincia) =>
  idProvincia === '' || idProvincia === null || idProvincia === undefined

const agregarIdPresupuestoAItems = (pedidoItems = [], idPresupuesto = 0) =>
  (pedidoItems || []).map((item) => ({
    ...item,
    idPresupuesto: Number(idPresupuesto) || 0
  }))

const PedidoDetalle = () => {
  const navigate = useNavigate()
  const { idPedido } = useParams()
  const { state } = useLocation()
  const dispatch = useDispatch()
  const topRef = useRef(null)
  const [percepciones, setPercepciones] = useState([])
  const [filasImpuestos, setFilasImpuestos] = useState([])
  const [erroresImpuestos, setErroresImpuestos] = useState({})
  const [modalYaMostrado, setModalYaMostrado] = useState(false)
  const [items, setItems] = useState([])
  const [pedidoState, setPedidoState] = useState(state?.pedido || {})
  const [mensajes, setMensajes] = useState([])
  const [costoEnvioManual, setCostoEnvioManual] = useState(0)
  const [tieneWFCompraComprador, setTieneWFCompraComprador] = useState(null)
  const [itemsMP, setItemsMP] = useState({})
  const [cargarPercepciones, setCargarPercepciones] = useState(true)

  const { loginDataContext: { idUsuario: usuario, empid } = {}, refrescarLimitesCompra } = useAuth()

  const cancelar = ![ID_ESTADO_ENTREGADO].includes(items[0]?.idPedidoItemEstado)

  const obtenerPedido = async () => {
    try {
      const respuesta = await pedidosService.buscarPorIdPedido(idPedido)
      const data = respuesta?.data

      if (!data?.pedidoItems?.length) {
        toastErrorLog(
          null,
          `No se encontraron datos del pedido Nº ${idPedido} o el pedido fue eliminado`,
          'pedidos'
        )
        navigate('/Pedidos', { replace: true })
        return
      }
      setItems(agregarIdPresupuestoAItems(data.pedidoItems, data.idPresupuesto))
      setPedidoState({
        idPedido,
        idComprador: data.idComprador,
        idVendedor: data.idVendedor,
        idPresupuesto: data.idPresupuesto,
        fechaPedido: data.fechaPedido,
        esVentas: data.idVendedor === usuario
      })
    } catch (error) {
      toastErrorLog(error, `Ocurrió un error al obtener el pedido Nº ${idPedido}`, 'pedidos')
      navigate('/Pedidos', { replace: true })
    }
  }

  useEffect(() => {
    if (state?.pedido) {
      setItems(agregarIdPresupuestoAItems(state.pedido.pedidoItems, state.pedido.idPresupuesto))
      setPedidoState({
        ...state.pedido,
        esVentas: state.esVentas ?? false
      })
    } else {
      obtenerPedido()
    }
  }, [state?.pedido, idPedido])

  useEffect(() => {
    setModalYaMostrado(false)

    topRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' })
    window.scrollTo(0, 0)
  }, [idPedido])

  useEffect(() => {
    if ((state?.mostrarModalImpuestos || state?.requiereMensajeMontoEnvio) && !modalYaMostrado) {
      let body
      if (state?.requiereMensajeMontoEnvio && state?.mostrarModalImpuestos) {
        body =
          'Para continuar, comuníquese con el vendedor y defina el costo de envío y los impuestos aplicables. Si corresponde, podrá adjuntar la documentación necesaria para exenciones o percepciones.'
      } else if (state?.requiereMensajeMontoEnvio) {
        body = 'Para continuar, comuníquese con el vendedor y defina el costo de envío aplicable.'
      } else {
        body =
          'Para continuar, comuníquese con el vendedor y defina los impuestos aplicables. Si corresponde, podrá adjuntar la documentación necesaria para exenciones o percepciones.'
      }

      setTimeout(() => {
        dispatch(
          setModal({
            show: true,
            title: 'Observación de montos',
            body: body,
            type: 'mensaje',
            size: 'sm'
          })
        )
        setModalYaMostrado(true)
      }, 100)
    }
  }, [state?.mostrarModalImpuestos, state?.requiereMensajeMontoEnvio, modalYaMostrado, dispatch])

  if (!pedidoState) {
    return null
  }

  const { idComprador, idVendedor, idPresupuesto } = pedidoState
  const empidVendedor = items?.[0]?.empidVendedor

  const validaCampos = (filas) => {
    const errores = {}
    ;(filas ?? []).forEach((f, i) => {
      const esNueva = !f.existente

      const vaciaNueva =
        esNueva &&
        provinciaSinSeleccionar(f.idProvincia) &&
        (f.alicuota === '' || f.alicuota === null) &&
        (!f.itemsBaseIds || f.itemsBaseIds.length === 0)

      if (vaciaNueva) {
        return
      }

      if (esNueva && provinciaSinSeleccionar(f.idProvincia)) {
        errores[`provincia_${i}`] = 'Debe seleccionar una provincia'
      }

      if (!f.pendienteEliminar && (!f.alicuota || f.alicuota <= 0 || f.alicuota > ALICUOTA_MAX)) {
        errores[`alicuota_${i}`] =
          `La alicuota debe ser mayor a 0 y menor o igual a ${ALICUOTA_MAX}`
      }

      if (esNueva && (!f.itemsBaseIds || f.itemsBaseIds.length === 0)) {
        errores[`items_${i}`] = 'Debe seleccionar al menos un ítem para calcular la base imponible'
      }
    })

    const provinciasExistentes = new Set()
    ;(filas ?? []).forEach((f, i) => {
      if (f.pendienteEliminar) {
        return
      }
      const id = f.idProvincia
      if (provinciaSinSeleccionar(id)) {
        return
      }

      const key = `${id}`
      if (provinciasExistentes.has(key)) {
        errores[`provincia_${i}`] = 'Ya existe una alicuota para la provincia seleccionada'
      } else {
        provinciasExistentes.add(key)
      }
    })

    return errores
  }

  const esMayorista = (item) => {
    if (!item?.preciosMayoristas || item.preciosMayoristas.length === 0) {
      return null
    }
    const tiersValidos = item.preciosMayoristas
      .map((p) => ({
        cantidad: p.cantidad,
        precioUnitario: p.precioUnitario
      }))
      .filter((p) => p.cantidad > 0 && p.precioUnitario > 0)

    if (tiersValidos.length === 0) {
      return null
    }

    const aplicables = tiersValidos.filter((tier) => item.cantidad >= tier.cantidad)
    if (aplicables.length === 0) {
      return null
    }

    return aplicables.reduce((prev, curr) => (curr.cantidad > prev.cantidad ? curr : prev))
  }

  const valorUnitarioSinIva = (item) => {
    const mayorista = esMayorista(item)
    if (mayorista) {
      return mayorista.precioUnitario
    }

    const unitario = item.precioUnitario
    if (unitario > 0) {
      return unitario
    }
    const totalConIva = item.valorTotalConIva
    const cant = item.cantidad || 1
    return cant > 0 ? totalConIva / cant : 0
  }

  const total = useMemo(
    () => items.reduce((acc, item) => acc + calcularTotalItemConIva(item), 0),
    [items]
  )

  const totalSinIva = items.reduce((acc, item) => acc + calcularTotalItemSinIva(item), 0)

  const costoEnvioCalculado = (() => {
    const preciosUnicos = new Set()

    items.forEach((item) => {
      const precio = Number(item.precioEnvio) || 0
      if (precio > 0) {
        preciosUnicos.add(precio)
      }
    })

    return preciosUnicos.size > 0 ? Math.max(...Array.from(preciosUnicos)) : 0
  })()

  useEffect(() => {
    setCostoEnvioManual(costoEnvioCalculado)
  }, [idPedido, costoEnvioCalculado])

  const costoEnvioTotal = pedidoState?.esVentas
    ? Number(costoEnvioManual) || 0
    : costoEnvioCalculado

  const netoTotalPedido = totalSinIva + costoEnvioTotal

  useEffect(() => {
    if (items?.length > 0) {
      const pedidoItems = new Set()
      let anticipo = 0

      const itemsMP = items.map((item) => {
        pedidoItems.add(item.idPedidoItem)
        anticipo = !item.anticipo
          ? 100
          : item.idPagoEstado === PAGO_ANTICIPO
            ? 100 - item.anticipo
            : item.anticipo

        return {
          title: item.titulo,
          quantity: item.cantidad,
          unit_price: item.precioUnitario * (1 + item.alicuota / 100) * (anticipo / 100),
          fee_percentage: item.esTiendaOficial
            ? (item.porcentajeExposicion + AUMENTO_TIENDA_OFICIAL) / 100
            : item.porcentajeExposicion / 100
        }
      })

      setItemsMP({
        items: itemsMP,
        orderId: idPedido,
        precioEnvio: items[0]?.precioEnvio * IVA_ENVIO * (anticipo / 100) || 0,
        percepciones: percepciones.reduce(
          (acc, item) => acc + item.baseImponible * (item.alicuota / 100) * (anticipo / 100),
          0
        ),
        metadata: {
          pedidoItems: [...pedidoItems]
        },
        email: items[0]?.vendedorEMail || '',
        idVendedor
      })
    }
  }, [percepciones, items])

  const esCtaCte = items[0]?.idMedioPago === CTA_CTE

  const correspondeEnvioCosto =
    items?.filter((item) => item.precioEnvio === 0 && item.idMedioEnvio === ENVIO_A_CARGO_COMPRADOR)
      .length >= MIN_ITEMS_ENVIO_A_CARGO ||
    items?.filter(
      (item) =>
        (item.idPedidoItemEstado === ID_ESTADO_MONTOS_NO_OK ||
          item.idPedidoItemEstado === ID_ESTADO_MONTOS_OK) &&
        item.idMedioEnvio === ENVIO_A_CARGO_COMPRADOR
    ).length >= MIN_ITEMS_ENVIO_A_CARGO

  const costoEnvioInvalido =
    correspondeEnvioCosto &&
    (costoEnvioManual === '' || costoEnvioManual === null || Number(costoEnvioManual) <= 0)

  const quedaNoConfirmado = esCtaCte && correspondeEnvioCosto && tieneWFCompraComprador === 'S'

  const obtenerImpuestos = async () => {
    const { data } = await impuestoService.buscarTodos(idPedido)
    setPercepciones(data)
    setFilasImpuestos(
      data.map((i) => ({
        ...i,
        existente: true,
        pendienteEliminar: false,
        itemsBaseIds: []
      }))
    )
    setErroresImpuestos({})
    setCargarPercepciones(false)
  }

  const buscarMensajes = async () => {
    if (!idComprador) {
      return
    }

    try {
      const respuesta = await mensajesService.buscarPorPedido(idPedido, idComprador)
      const data = respuesta?.data

      setMensajes(
        data?.length
          ? [...data].sort((a, b) => new Date(a.fechaEscrito) - new Date(b.fechaEscrito))
          : []
      )
    } catch {
      setMensajes([])
    }
  }

  const buscarTieneWFCompraComprador = async () => {
    try {
      const { data } = await usuarioService.buscarPorId(idComprador)
      setTieneWFCompraComprador(data?.tieneWFCompra ?? false)
    } catch {
      setTieneWFCompraComprador(false)
    }
  }

  useEffect(() => {
    if (idComprador) {
      obtenerImpuestos()
      buscarMensajes()
      buscarTieneWFCompraComprador()
    }
  }, [idComprador, idPedido])

  const actualizarPedido = async () => {
    try {
      if (items.length === 0) {
        return
      }
      const respuesta = await pedidosService.buscarPorPedidoItem(items[0].idPedidoItem)
      const data = respuesta?.data
      if (!data?.pedidoItems?.length) {
        return
      }
      setItems(agregarIdPresupuestoAItems(data.pedidoItems, data.idPresupuesto || idPresupuesto))
    } catch (error) {
      const mensaje = `Ocurrió un error al actualizar el pedido.`
      toastErrorLog(error, mensaje, 'compra')
    }
  }

  const notificar = async (observacionImpuestos = false, quedaNoConfirmado = false) => {
    try {
      let nuevosItems = [...items]
      let msg = ''
      if (esCtaCte) {
        msg = 'Se cargó el monto del costo de envío.'
      } else if (observacionImpuestos) {
        msg = 'Por favor, revisá el pedido, ya que presenta observaciones.'
      } else {
        msg = 'Se actualizó el detalle del pedido. Por favor, revisar los cambios.'
      }

      const mensaje = {
        idMensajePadre: null,
        idPublicacion: null,
        idPedido: idPedido,
        idPresupuesto: null,
        idComprador,
        idVendedor,
        idUsuarioChat: observacionImpuestos ? idVendedor : idComprador,
        idUsuarioEmisor: usuario,
        texto: msg,
        privado: true,
        idAdjunto: null
      }

      await mensajesService.insertar(mensaje)

      for (const item of items) {
        const estadoActualizado = {
          idPedidoItem: item.idPedidoItem,
          idVendedor,
          idPedidoItemEstado: quedaNoConfirmado
            ? ID_ESTADO_NO_CONFIRMADO
            : esCtaCte
              ? ID_ESTADO_PEDIDO_CREADO
              : observacionImpuestos
                ? ID_ESTADO_MONTOS_NO_OK
                : ID_ESTADO_EN_ESPERA
        }

        const { data: itemActualizado } =
          await pedidosService.actualizarIdPedidoEstadoItem(estadoActualizado)

        nuevosItems = nuevosItems.map((i) =>
          i.idPedidoItem === itemActualizado.idPedidoItem ? { ...i, ...itemActualizado } : i
        )
      }

      setItems(nuevosItems)
    } catch (error) {
      const mensaje = `Ocurrió un error al enviar la notificación.`
      toastErrorLog(error, mensaje, 'compra')
    }
  }

  const observarImpuestos = async () => {
    await notificar(true, quedaNoConfirmado)
    await buscarMensajes()
    toast.success('Se enviaron las observaciones sobre los cambios realizados')
  }

  const ingresarPercepciones = async () => {
    const errores = validaCampos(filasImpuestos)
    if (Object.keys(errores).length > 0) {
      setErroresImpuestos(errores)
      return false
    }
    const nuevas = filasImpuestos.filter(
      (f) =>
        !f.existente &&
        !provinciaSinSeleccionar(f.idProvincia) &&
        f.alicuota !== '' &&
        f.alicuota !== null
    )

    const eliminadas = filasImpuestos.filter((f) => f.existente && f.pendienteEliminar)
    const actualizables = filasImpuestos.filter((f) => f.existente && !f.pendienteEliminar)

    await insertarNuevas(nuevas)
    await eliminarExistentes(eliminadas)
    await actualizarExistentes(actualizables)

    const { data } = await impuestoService.buscarTodos(idPedido)
    setPercepciones(data)

    setFilasImpuestos(
      data.map((i) => ({
        ...i,
        existente: true,
        pendienteEliminar: false,
        itemsBaseIds: []
      }))
    )

    setErroresImpuestos({})
    return true
  }

  const insertarNuevas = async (nuevas) => {
    try {
      if (nuevas.length === 0) {
        return
      }

      for (const f of nuevas) {
        const baseImponible = f.baseImponible || netoTotalPedido
        await impuestoService.insertar({
          idPedido,
          idProvincia: f.idProvincia,
          tipoImpuesto: TIPO_IMPUESTO_PERCEPCION,
          alicuota: f.alicuota,
          baseImponible: baseImponible,
          monto: recalcularMonto(baseImponible, f.alicuota)
        })
      }
    } catch (error) {
      const mensaje = `Ocurrió un error al insertar las percepciones.`
      toastErrorLog(error, mensaje, 'impuesto')
    }
  }

  const eliminarExistentes = async (eliminadas) => {
    if (eliminadas.length === 0) {
      return
    }

    try {
      for (const f of eliminadas) {
        await impuestoService.eliminar({
          idPedido,
          idProvincia: f.idProvincia,
          tipoImpuesto: TIPO_IMPUESTO_PERCEPCION
        })
      }
    } catch (error) {
      const mensaje = `Ocurrió un error al eliminar las percepciones.`
      toastErrorLog(error, mensaje, 'impuesto')
    }
  }

  const actualizarExistentes = async (actualizables) => {
    if (actualizables.length === 0) {
      return
    }

    try {
      for (const f of actualizables) {
        await impuestoService.actualizar({
          idPedido,
          idProvincia: f.idProvincia,
          tipoImpuesto: TIPO_IMPUESTO_PERCEPCION,
          alicuota: f.alicuota,
          baseImponible: f.baseImponible,
          monto: recalcularMonto(f.baseImponible, f.alicuota)
        })
      }
    } catch (error) {
      const mensaje = `Ocurrió un error al actualizar las percepciones.`
      toastErrorLog(error, mensaje, 'impuesto')
    }
  }

  const guardarCostoEnvio = async () => {
    try {
      const costoNormalizado = Number(costoEnvioManual) || 0

      const itemsConCosto = items.map((item) => ({
        ...item,
        entregaLocalidad: Number(item.entregaLocalidad) || 0,
        idMoneda: item.idMoneda || 1,
        precioEnvio:
          item.idMedioEnvio === ENVIO_A_CARGO_COMPRADOR
            ? costoNormalizado
            : Number(item.precioEnvio) || 0
      }))

      const itemsAActualizar = itemsConCosto.filter(
        (item) => item.idMedioEnvio === ENVIO_A_CARGO_COMPRADOR
      )

      if (itemsAActualizar.length === 0) {
        return
      }

      setItems(itemsConCosto)
      await pedidosService.actualizarItemsMultiples(itemsAActualizar)
    } catch (error) {
      const mensaje = `Ocurrió un error al guardar el costo de envío.`
      toastErrorLog(error, mensaje, 'envio')
      throw error
    }
  }

  const confirmarYEnviar = async () => {
    try {
      if (costoEnvioInvalido) {
        return
      }

      if (correspondeEnvioCosto) {
        await guardarCostoEnvio()
      }

      const ok = await ingresarPercepciones()
      if (!ok) {
        return
      }

      await notificar(false, quedaNoConfirmado)
      if (!pedidoState.esVentas) {
        await actualizarPedido()
      }
      await buscarMensajes()
      toast.success('Se confirmó y envió correctamente al comprador')
    } catch (error) {
      const mensaje = `Ocurrió un error al confirmar y enviar el pedido.`
      toastErrorLog(error, mensaje, 'compra')
    }
  }

  const habilitarBotonVendedor = items.some(
    (item) =>
      item.idPedidoItemEstado === ID_ESTADO_MONTOS_OK ||
      item.idPedidoItemEstado === ID_ESTADO_MONTOS_NO_OK
  )

  const habilitarBotonComprador = items.some(
    (item) => item.idPedidoItemEstado === ID_ESTADO_EN_ESPERA
  )

  const habilitarBotonCancelar = items.some(
    (item) =>
      ![ID_ESTADO_CANCELADO, ID_ESTADO_DEVUELTO, ID_ESTADO_ENTREGA_OBSERVADA].includes(
        item.idPedidoItemEstado
      )
  )

  const openConfirm = (texto) => {
    dispatch(
      setModal({
        size: 'sm',
        title: `Confirmar ${texto}`,
        type: 'botones',
        body: `¿Estás seguro de que querés ${texto} el pedido en su totalidad?`,
        show: true
      })
    )
  }

  const eliminarPedido = async () => {
    try {
      const tipoAccion = cancelar ? ID_ESTADO_CANCELADO : ID_ESTADO_DEVUELTO

      items?.[0]?.idPagoEstado === PAGO_OK &&
        (await mercadoPagoService.refund({ idPedido, empid: empidVendedor }))
      await pedidosService.eliminarPedido(idPedido, idVendedor, tipoAccion)

      if (Number(idComprador) === Number(usuario)) {
        await refrescarLimitesCompra(usuario)
      }

      toast.success('Pedido eliminado correctamente')
      setTimeout(() => navigate('/Pedidos'), 100)
      return true
    } catch (error) {
      const mensaje = `Ocurrió un error al eliminar el pedido.`
      toastErrorLog(error, mensaje, 'pedidos')
      return false
    }
  }

  const [adjuntoPendiente, setAdjuntoPendiente] = useState(null)
  const [subiendoAdjunto, setSubiendoAdjunto] = useState(false)

  useEffect(() => {
    const fetchAdjuntoFactura = async () => {
      if (!idPedido || !pedidoState) return
      const idParaBuscar = pedidoState.esVentas ? usuario : pedidoState.idVendedor
      if (!idParaBuscar) return
      const { data } = await archivoAdjuntoService.buscarUltimoAdjunto(
        idParaBuscar,
        ADJUNTO_FACTURA,
        idPedido
      )
      const adj = data?.adjunto || data
      if (!adj?.idAdjunto) return
      const { data: dataAdj } = await archivoAdjuntoService.buscarAdjuntosPorId(adj.idAdjunto)
      const { dataAdjunto, adjunto: datosAdjunto } = dataAdj
      const previewAdjunto = await adjuntoBuilder(
        dataAdjunto,
        datosAdjunto,
        datosAdjunto?.archivo,
        { preview: true }
      )
      setAdjuntoPendiente({ ...previewAdjunto, idAdjunto: adj.idAdjunto })
    }
    if (idPedido && pedidoState) {
      fetchAdjuntoFactura()
    }
  }, [idPedido, usuario, pedidoState])

  const handleCarga = async (adjunto) => {
    try {
      setSubiendoAdjunto(true)
      const idAdjunto =
        adjunto?.data?.idAdjunto || adjunto?.data?.id || adjunto?.data?.adjunto?.idAdjunto
      if (!idAdjunto) {
        toast.error('No se pudo obtener el ID del adjunto')
        return
      }
      const { data } = await archivoAdjuntoService.buscarAdjuntosPorId(idAdjunto)
      const { dataAdjunto, adjunto: datosAdjunto } = data
      const previewAdjunto = await adjuntoBuilder(
        dataAdjunto,
        datosAdjunto,
        datosAdjunto?.archivo,
        { preview: true }
      )
      setAdjuntoPendiente({ ...previewAdjunto, idAdjunto })
    } catch (error) {
      toastErrorLog(error, 'Error al procesar el adjunto', 'adjunto')
    } finally {
      setSubiendoAdjunto(false)
    }
  }

  const handleEliminar = () => {
    setAdjuntoPendiente(null)
  }

  return (
    <BordeDetalle
      className={styles.pedido}
      titulo={`Pedido Nº ${idPedido}`}
      onCerrarClick={() => navigate(pedidoState?.esVentas ? '/Ventas' : '/Pedidos')}
      ref={topRef}
    >
      <DetallePrecio
        pedidoItems={items}
        valorUnitarioSinIva={valorUnitarioSinIva}
        esMayorista={esMayorista}
        percepciones={percepciones}
        total={total}
        totalSinIva={totalSinIva}
        costoEnvio={costoEnvioTotal}
        usaImagen={false}
        esVentas={pedidoState.esVentas}
        pedidoCompletoEsCtaCte={esCtaCte}
        impuestosConfig={{
          filasImpuestos,
          onChangeFilasImpuestos: setFilasImpuestos,
          erroresImpuestos,
          setErroresImpuestos
        }}
        costoEnvioConfig={{
          onChangeCostoEnvio: setCostoEnvioManual,
          errorCostoEnvio: costoEnvioInvalido
            ? 'Debes ingresar el costo de envío antes de confirmar.'
            : null,
          correspondeEnvioCosto: correspondeEnvioCosto
        }}
      />

      {![undefined, null].includes(items[0]?.idPagoEstado) &&
        ![PAGO_OK, ID_ESTADO_CANCELADO, ID_ESTADO_DEVUELTO].includes(items[0]?.idPagoEstado) &&
        habilitarBotonCancelar &&
        (items[0]?.medioPago === 'Mercado Pago' || items[0]?.idMedioPago === MERCADO_PAGO) &&
        !pedidoState.esVentas &&
        items[0]?.idPedidoItemEstado !== ID_ESTADO_MONTOS_OK &&
        items[0]?.idPedidoItemEstado !== ID_ESTADO_MONTOS_NO_OK &&
        itemsMP?.items?.length > 0 &&
        !cargarPercepciones &&
        ((itemsMP.percepciones && percepciones.length > 0) || percepciones.length === 0) && (
          <PasarelaMP items={itemsMP} />
        )}

      <div className="text-center w-100 mb-2 d-flex justify-content-center mt-3">
        {pedidoState.esVentas ? (
          habilitarBotonVendedor && (
            <button
              type="button"
              className={styles.btnConfirmar}
              onClick={confirmarYEnviar}
              disabled={costoEnvioInvalido}
            >
              Confirmar y notificar comprador
            </button>
          )
        ) : (
          <>
            {habilitarBotonCancelar && (
              <button
                type="button"
                className={styles.btnDanger}
                onClick={() => openConfirm(cancelar ? 'Cancelar' : 'Devolver')}
              >
                {cancelar ? 'Cancelar' : 'Devolver'} compra
              </button>
            )}
            {habilitarBotonComprador && (
              <button
                type="button"
                className={styles.btnObservar}
                onClick={observarImpuestos}
                title="Se le notificará al vendedor la revisión de los cambios"
              >
                Observar cambios
              </button>
            )}
          </>
        )}
      </div>
      <GenericModal onConfirm={eliminarPedido} />

      <MensajeriaNew
        titulo={'Mensajería interna'}
        id={Number(idPedido)}
        tipoDoc={'pedidoCabecera'}
        idVendedor={idVendedor}
        idComprador={idComprador}
        mensajes={mensajes}
        tipoMensajes={'chat'}
        usuario={usuario}
        actualizarMensajes={buscarMensajes}
        idMensajeTipo={
          pedidoState.esVentas ? MENSAJE_TIPO_PEDIDO_COMPRA : MENSAJE_TIPO_PEDIDO_VENTA
        }
        pedidoEntregado={!habilitarBotonCancelar}
        empid={empid}
      />
      <div className={styles.uploadBox}>
        <span className={styles.uploadBoxIcon} aria-hidden="true">
          <i className="bi bi-file-earmark-arrow-up"></i>
        </span>
        <h5 className={styles.adjuntarFacturaTitulo}>
          {pedidoState.esVentas ? 'Adjuntar factura' : 'Factura de la compra'}
        </h5>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {pedidoState.esVentas && (
            <>
              <Upload
                idDoc={Number(idPedido)}
                idTipo={11}
                idPerfil={usuario}
                onCarga={handleCarga}
                adjuntoPendiente={adjuntoPendiente ? [adjuntoPendiente] : []}
              />
              {subiendoAdjunto && (
                <span className={styles.nombreAdjuntoCargado} style={{ color: '#888' }}>
                  Subiendo adjunto...
                </span>
              )}
            </>
          )}
          {adjuntoPendiente && !subiendoAdjunto && (
            <span className={styles.nombreAdjuntoCargado} title={adjuntoPendiente.nombre}>
              <i
                className="bi bi-paperclip"
                style={{ marginRight: 4, color: 'var(--color-primary)' }}
              />
              <a
                href={adjuntoPendiente.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.archivoLink}
              >
                {adjuntoPendiente.nombre || 'Archivo'}
              </a>
              {pedidoState.esVentas && (
                <button
                  type="button"
                  className={styles.removeAdjuntoBtn}
                  onClick={handleEliminar}
                  title="Eliminar adjunto"
                  aria-label="Eliminar adjunto"
                >
                  <i className="bi bi-trash" />
                </button>
              )}
            </span>
          )}
          {!adjuntoPendiente && !subiendoAdjunto && !pedidoState.esVentas && (
            <span className={styles.nombreAdjuntoCargado} style={{ color: '#888' }}>
              Factura no disponible
            </span>
          )}
        </div>
      </div>
    </BordeDetalle>
  )
}

export default PedidoDetalle
